<script lang="ts" setup>
  import { useQuery } from '@tanstack/vue-query'

  import { useGolfsService } from '~/core/services'
  import type { FetchErrorObj } from '~/core/types'
  import { BngContentFit, BngModal, BngPageFooter } from '~/layouts/components'

  const { nearbyGolfs } = useNearbyGolfs()
  const { displayToast } = useUiStore()
  const router = useRouter()
  const { matches: matchDesktop } = useMatchMedia(ScreenWidth.desktop)
  const bookingModal = ref(false)
  const route = useRoute()
  const { getGolf } = useGolfsService()

  const golfId = computed(() => getIdFromSlug('golfSlug' in route.params ? route.params.golfSlug as string : null))

  const canFetchGolf = computed(() => !isNaN(parseInt(golfId.value)))

  async function fetchGolf(golfId: string) {
    try {
      return await getGolf(golfId)
    } catch (e) {
      const { status } = e as FetchErrorObj<unknown>
      if (status === 404) {
        displayToast('Le golf n\'est pas disponible', 'warning', 3000)
        router.push('/')
        return null
      }
      throw e
    }
  }

  const { data: golf } = useQuery({
    enabled: canFetchGolf,
    queryKey: ['golf', golfId],
    queryFn: () => fetchGolf(golfId.value),
  })

  const golfCourses = computed(() => golf.value?.data?.courses ?? [])
  const golfName = computed(() => golf.value?.data?.name ?? '')

  const breadcrumb = computed(() => {
    if (!golf.value?.data) {
      return []
    }
    return [
      {
        label: 'Green fee',
        link: `/search/green-fee/country/fr`,
      },
      {
        label: golf.value.data.region.name,
        link: `/region/${golf.value.data.region.id}-${golf.value.data.region.slug}`,
      },
      {
        label: golf.value.data.name,
        link: null,
      },
    ]
  })

  useHead(() => ({
    title: golf.value?.data?.name ? `Bookandgolf | ${golf.value?.data?.name}` : 'Bookandgolf | Golf',
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: `Réservez votre départ au meilleur prix pour ${golf.value?.data?.name}. Réservation 7j/7 24h/24`,
      },
    ],
  }))

</script>
<template>
  <div class="p-golf">
    <template v-if="golf?.data">
      <Teleport
        v-if="bookingModal"
        to="#modal-booking"
      >
        <BngModal
          closable
          :foreground="matchDesktop"
          @close="bookingModal = false"
        >
          <GolfDetailReservation
            :courses="golfCourses"
            :golf-name="golfName"
            @close="bookingModal = false"
          />
        </BngModal>
      </Teleport>
      <div class="golf-wrapper">
        <BngContentFit small>
          <Breadcrumb :items="breadcrumb" />
          <div class="content">
            <GolfDetailContent
              :golf="golf.data"
            />
            <ClientOnly>
              <GolfDetailReservation
                v-if="matchDesktop"
                :courses="golfCourses"
                :golf-name="golfName"
              />
            </ClientOnly>
          </div>
        </BngContentFit>

        <!--      MOBILE ONLY -->
        <Button
          class="see-courses action-footer"
          @click="bookingModal = true"
        >
          <span class="title">
            Voir les départs
            <SvgIcon
              icon="arrow-nav"
              :width="24"
              :height="20"
            />
          </span>
        </Button>
      </div>
      <div
        v-if="nearbyGolfs?.data.length"
        class="more-golfs"
      >
        <BngContentFit>
          <div class="more-golfs-title">
            <SvgIcon
              :height="20"
              :width="20"
              :desktop-height="30"
              :desktop-width="30"
              icon="sight"
            />
            <p class="title">
              <span>Plus de golfs</span>
            </p>
          </div>
          <div class="scroll-container">
            <ScrollH class="more-golfs-cards">
              <Paper
                v-for="item of nearbyGolfs.data"
                :key="item.id"
                class="hover-shadow"
              >
                <GolfCardWrapper
                  :golf="item"
                  is-home
                />
              </Paper>
            </ScrollH>
          </div>
        </BngContentFit>
      </div>
      <BngPageFooter />
    </template>
  </div>
</template>
<style scoped>
.p-golf {
  display: flex;
  width: 100%;
  flex: 0 0 auto;
  flex-direction: column;
  justify-content: center;

  --breacrumb-height: 60px;
  --displayed-more-golfs: 3;

  .reservation-component-wrapper {
    position: absolute;
    background-color: white;
    inset: 0;

    .close-btn {
      position: absolute;
      top: 1em;
      right: 1.5em;
      width: 1em;
      height: 1em;
      cursor: pointer;
      opacity: 0.6;
    }
  }

  .golf-wrapper {
    flex: 1 0 auto;

    .lc-content-fit {
      padding: 0;
    }

    .c-breadcrumb {
      display: none;
      height: var(--breacrumb-height);
      align-items: center;
    }

    .content {
      display: flex;
      flex-direction: column;
      margin: auto;
    }

    .action-footer {
      position: sticky;
      z-index: 20;
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      width: 100vw;
      justify-content: center;
      padding: 1rem;
      background-color: var(--color-primary);
      box-shadow: var(--box-shadow-1);
      cursor: pointer;
      letter-spacing: 1.76px;
      text-transform: uppercase;

      .c-icon {
        margin-left: 0.5em;
        transform: rotate(0.75turn);
      }
    }

    .see-courses {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      padding: 1em;
      background-color: var(--color-primary);
      cursor: pointer;

      &:hover {
        .title {
          .c-icon {
            margin-left: 0.75em;
          }
        }
      }

      .title {
        display: flex;
        flex-direction: row;
        color: var(--color-secondary);
        font-family: Bariol, sans-serif;
        font-size: 1.375em;
        font-weight: 700;
        letter-spacing: 1.5px;
        text-transform: uppercase;

        .c-icon {
          margin-left: 0.5em;
          transform: rotate(-0.25turn);
          transition: margin-left ease-out 0.25s;
        }
      }
    }
  }

  .more-golfs {
    display: none;
  }

  .c-page-footer {
    display: none;
  }

  @media screen and (width >= 1024px) {
    padding-bottom: 0;

    --breacrumb-height: 80px;
    --displayed-more-golfs: 3;

    .golf-wrapper {
      .see-courses {
        display: none;
      }

      .lc-content-fit {
        padding: 0 2.5em;
      }

      .c-breadcrumb {
        display: flex;
      }

      .content {
        position: relative;
        display: flex;
        width: 100%;
        flex-direction: row;
        margin-bottom: 6em;

        .c-golf-detail {
          flex: 1 1 40%;
        }

        .c-golf-detail-reservation {
          position: sticky;
          top: var(--breacrumb-height);
          flex: 1 1 60%;
        }
      }

      .c-page-footer {
        width: 100%;
        margin-top: 4em;
      }

      .action-footer {
        display: none;
      }
    }

    .more-golfs {
      position: relative;
      z-index: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 2.5em 0;
      margin-bottom: 2em;
      background-color: var(--color-secondary-light);

      .more-golfs-title {
        display: flex;
        flex-direction: row;
        padding-left: 1.5em;

        .c-icon {
          margin-right: 0.75em;
        }

        .title {
          span {
            color: var(--color-secondary);
            font-family: Bariol, sans-serif;
            font-size: 2em;
            font-weight: 700;
          }
        }
      }

      .scroll-container {
        position: relative;

        .more-golfs-cards {
          position: relative;
          z-index: 0;
          display: flex;
          max-width: calc(100vw - 8.5em);
          box-sizing: content-box;
          flex-direction: row;
          padding: 1.5em;
          background-color: var(--color-secondary-light);
          grid-gap: 1.5em;

          .c-paper {
            overflow: hidden;
            flex: 0 0 calc((100% / var(--displayed-more-golfs)) - var(--gap));
            margin: 0;

            --gap: 1.5em * (var(--displayed-more-golfs) - 1) / var(--displayed-more-golfs);
          }
        }

        &::after,
        &::before {
          position: absolute;
          z-index: 1;
          bottom: 0;
          width: 1.5em;
          height: 100%;
          content: "";
        }

        &::after {
          right: 0;
          background: linear-gradient(to left, var(--color-secondary-light), transparent);
        }

        &::before {
          left: 0;
          background: linear-gradient(to right, var(--color-secondary-light), transparent);
        }
      }
    }

    .c-page-footer {
      display: flex;
    }
  }

  @media screen and (width >= 1280px) {
    --displayed-more-golfs: 4;
  }

  @media screen and (width >= 1600px) {
    --displayed-more-golfs: 5;
  }
}
</style>
